import { Application, Assets, Texture } from 'pixi.js'
import { useEffect, useRef } from 'react'

import { ParticleField } from '../game/particles'
import { log } from '../log'
import type { ResolvedTheme, StageEmitter } from '../theme'

/**
 * Live particles pinned to the painted backdrop: water falling down a
 * waterfall in the art, mist at its foot.
 *
 * The backdrop itself stays a CSS `background-image` — it never moves, and
 * a full-screen sprite would cost fill rate every frame for a still
 * picture. Only the moving part is WebGL, on a transparent canvas over it.
 *
 * The hard part is not the particles, it is staying on the waterfall. The
 * backdrop is `background-size: cover`, so on a wide viewport the top and
 * bottom of the picture are cropped and on a tall one the sides are. An
 * emitter placed in viewport coordinates would drift off the painted
 * waterfall on any screen but the one it was authored on. So emitters are
 * authored in fractions of the IMAGE, and this component reproduces the
 * cover transform to place them — the same maths the browser applies to
 * the layer underneath.
 */

interface Props {
  theme: ResolvedTheme
}

/** Geometry of `background-size: cover` for one image in one viewport. */
interface Cover {
  scale: number
  offsetX: number
  offsetY: number
  imageWidth: number
  imageHeight: number
}

function coverFit(iw: number, ih: number, vw: number, vh: number): Cover {
  const scale = Math.max(vw / iw, vh / ih)
  return {
    scale,
    offsetX: (vw - iw * scale) / 2,
    offsetY: (vh - ih * scale) / 2,
    imageWidth: iw,
    imageHeight: ih,
  }
}

/** Spawn budget carried between frames, so a low rate is not rounded to 0. */
interface EmitterState {
  emitter: StageEmitter
  pending: number
  /** Free-running phase, for the sine drift of the mist. */
  phase: number
  /**
   * Whether the first frame's back-fill has run. Clouds cross the scene in
   * half a minute, so without it the sky starts empty and slowly fills —
   * the player would watch the effect switch itself on.
   */
  seeded: boolean
  /** `glow`: phase at which the next breath and the next facet flash fire. */
  nextPulse: number
  nextFlash: number
}

/** Viewport the layer covers, in CSS px. */
interface Screen {
  w: number
  h: number
}

export function StageEffects({ theme }: Props): React.JSX.Element | null {
  const hostRef = useRef<HTMLDivElement>(null)
  const emitters = theme.stage.emitters
  const backdrop = theme.stage.backgroundImage

  useEffect(() => {
    const host = hostRef.current
    if (!host || emitters.length === 0 || !backdrop) return

    // Decorative motion only: someone who asked the OS for less of it gets
    // the painted waterfall and nothing on top.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let app: Application | null = null
    let disposed = false

    const start = async (): Promise<void> => {
      // The natural size of the backdrop is what every emitter coordinate
      // is relative to, and only the loaded image can tell us.
      const image = new Image()
      image.src = backdrop
      try {
        await image.decode()
      } catch (error) {
        // A backdrop that fails to decode is already a visible problem on
        // the CSS layer; here it just means no particles.
        log.error('stage-fx.backdrop-decode', error, 'stage backdrop did not decode', { backdrop })
        return
      }
      if (disposed) return

      const created = new Application()
      await created.init({
        // The HOST, not the window: `window.innerWidth` includes the
        // scrollbar and the fixed backdrop box does not. Sizing to the
        // window makes this layer a few px wider than the picture it is
        // supposed to sit on, and every emitter drifts right — the more so
        // the further it is from the centre.
        resizeTo: host,
        backgroundAlpha: 0,
        antialias: false,
        // Particles are soft blobs — a retina buffer quadruples the fill
        // cost of a full-screen layer and no one can tell the difference.
        resolution: 1,
        autoDensity: true,
      })
      if (disposed) {
        created.destroy(true, { children: true })
        return
      }
      app = created
      host.appendChild(created.canvas)

      // One field per material, and the order they are added IS the depth
      // order of the scene: clouds drift behind everything, then the water,
      // then the haze it throws up, and the additive glows on top.
      //
      // They cannot share a field: a field has one blend mode and one
      // texture, and a cloud drawn with the glow sprite reads as fog while
      // a spark drawn without `add` reads as a grey dot.
      // Theme-shipped cloud PNGs, when the manifest lists any. A file that
      // fails to load is skipped rather than failing the whole sky — the
      // procedural fleet remains the fallback for an empty result.
      const cloudTextures: Texture[] = []
      // `?? []` although the type promises an array: this layer is pure
      // decoration, and a hand-built theme (debug pages, tests) must not
      // be able to take down every emitter by omitting one list.
      for (const src of theme.stage.cloudImages ?? []) {
        try {
          cloudTextures.push(await Assets.load<Texture>(src))
        } catch (error) {
          log.error('stage-fx.cloud-image', error, 'cloud image did not load', { src })
        }
      }
      if (disposed) return

      const fields: Record<StageEmitter['kind'], ParticleField> = {
        cloud: new ParticleField('normal', 'cloud', cloudTextures),
        waterfall: new ParticleField('normal'),
        mist: new ParticleField('normal'),
        glow: new ParticleField('add'),
      }
      // The gems' star flares. A separate field because a field carries one
      // texture, and the flare is the one part of the aura whose SHAPE does
      // the work — over painted gold and sky there is no headroom left for
      // a brighter blob, but there is nothing star-shaped to compete with.
      const flare = new ParticleField('add', 'star')
      created.stage.addChild(
        fields.cloud.view,
        fields.waterfall.view,
        fields.mist.view,
        fields.glow.view,
        flare.view,
      )

      const states: EmitterState[] = emitters.map((emitter, i) => ({
        emitter,
        pending: 0,
        // Staggered, so several waterfalls do not pulse in lockstep.
        phase: i * 1.7,
        seeded: false,
        // Same idea for the gems, and it matters more: four rubies
        // brightening on the same frame reads as a screen flash. The
        // modulo keeps the opening delay under a couple of seconds
        // however far down the list an emitter sits.
        nextPulse: i * 1.7 + ((i * 0.9) % 2.5),
        nextFlash: i * 1.7 + 2 + Math.random() * 4,
      }))

      created.ticker.add((ticker) => {
        const dt = Math.min(ticker.deltaMS, 50) / 1000
        const screen = { w: created.screen.width, h: created.screen.height }
        const fit = coverFit(image.naturalWidth, image.naturalHeight, screen.w, screen.h)
        for (const state of states) {
          state.phase += dt
          spawn(state, fit, screen, dt, fields[state.emitter.kind], flare)
        }
        for (const field of Object.values(fields)) field.update(ticker.deltaMS)
        flare.update(ticker.deltaMS)
      })
    }

    // Fullscreen hides this layer in CSS, and a hidden canvas still costs
    // what a visible one costs: the ticker keeps stepping every emitter and
    // the GPU keeps drawing frames nobody sees. Stop the clock instead.
    // Called once after start() too, so entering fullscreen before the
    // layer finished loading still leaves it stopped.
    const syncToFullscreen = (): void => {
      if (!app) return
      const visible = document.fullscreenElement == null
      if (visible) app.ticker.start()
      else app.ticker.stop()
    }
    document.addEventListener('fullscreenchange', syncToFullscreen)

    void start().then(syncToFullscreen)

    return () => {
      disposed = true
      document.removeEventListener('fullscreenchange', syncToFullscreen)
      app?.destroy(true, { children: true })
      app = null
    }
  }, [emitters, backdrop])

  if (emitters.length === 0 || !backdrop) return null
  return <div className="stage-fx" ref={hostRef} aria-hidden="true" />
}

/**
 * Emit one frame's worth of particles for a single source.
 *
 * Every speed and size is multiplied by the cover scale, so the effect
 * looks the same on a laptop and on a 4K monitor: the art is scaled there
 * too, and unscaled particles would read as drizzle over a big waterfall.
 */
function spawn(
  state: EmitterState,
  fit: Cover,
  screen: Screen,
  dt: number,
  field: ParticleField,
  flare: ParticleField,
): void {
  const e = state.emitter
  const s = fit.scale
  const x = fit.offsetX + e.x * fit.imageWidth * s
  const y = fit.offsetY + e.y * fit.imageHeight * s
  const width = e.width * fit.imageWidth * s
  const height = e.height * fit.imageHeight * s
  const curve = e.curve * fit.imageWidth * s

  if (e.kind === 'cloud') {
    spawnClouds(state, fit, screen, dt, field)
    return
  }

  if (e.kind === 'glow') {
    spawnGlow(state, x, y, width, height, s, dt, field, flare)
    return
  }

  // Rate is authored as density at the art's own scale; a cropped-in view
  // shows fewer sources but bigger ones, and this keeps the fall looking
  // equally dense either way.
  state.pending += e.rate * s * dt
  const count = Math.floor(state.pending)
  state.pending -= count
  if (count <= 0) return

  for (let i = 0; i < count; i++) {
    if (e.kind === 'mist') {
      // Fog blobs: wide, faint, slow, expanding. Built from the same
      // recipe as any smoke layer — a handful of very low-alpha discs with
      // a sine drift, which reads as organic without a noise texture.
      const drift = Math.sin(state.phase * 0.6 + i) * 10 * s
      // Mist gathers where the water LANDS, so it inherits the whole
      // sideways drift of the fall above it. It hugs the impact point: a
      // veil smeared over the whole island is invisible at its real
      // opacity, and this puff has to survive being drawn over bright
      // painted clouds.
      field.emit(x + curve + (Math.random() - 0.5) * width * 0.9, y + height * (0.85 + Math.random() * 0.25), {
        vx: drift + (Math.random() - 0.5) * 8 * s,
        vy: -(10 + Math.random() * 18) * s,
        life: 1.8 + Math.random() * 1.4,
        size: width * (0.45 + Math.random() * 0.5),
        gravity: 0,
        alpha0: (0.16 + Math.random() * 0.14) * e.alpha,
        color: e.color,
        spin: 0,
        grow: 1.0,
        fade: 'fog',
      })
      continue
    }

    // Water: a thin streak leaving the lip of the fall, accelerating down.
    // The lifetime is derived from the drop height so the sheet ends where
    // the painted one does instead of running past the island.
    const speed = (110 + Math.random() * 70) * s
    const gravity = 220 * s
    // Solve height = v0·t + ½·g·t² for t, instead of the naive height/v0.
    // The droplet accelerates, so the naive figure is the time it would
    // take at its INITIAL speed — with gravity on top it overshoots the
    // foot of the fall by half again, and the stream runs past the island.
    const drop = (-speed + Math.sqrt(speed * speed + 2 * gravity * height)) / gravity
    const life = drop * (0.85 + Math.random() * 0.3)
    field.emit(x + (Math.random() - 0.5) * width, y, {
      // The drift is spread over the particle's own lifetime, so a droplet
      // that dies early has covered exactly its share of the arc and the
      // whole stream traces one curve instead of fanning out.
      vx: curve / life + (Math.random() - 0.5) * 8 * s,
      vy: speed,
      life,
      size: Math.max(1.5, width * (0.07 + Math.random() * 0.09)),
      gravity,
      // Bright enough to read as foam over the painted water — at the old
      // 0.35 floor the only visible droplets were the ones that had left
      // the water, which made a perfectly placed fall look misplaced.
      alpha0: (0.55 + Math.random() * 0.35) * e.alpha,
      color: e.color,
      spin: 0,
      // Streaked, not round: a falling droplet smears over the frame it is
      // photographed in, and a round one reads as snow.
      stretch: 3 + Math.random() * 3,
      grow: 0,
      // Invisible at the lip and only solid a third of the way down: the
      // painted sheet is at its most opaque up there, so a droplet that
      // starts bright draws a seam exactly where the effect is supposed to
      // disappear into the art.
      fade: 'fall',
      // No drag: it would eat the sideways drift the arc is made of.
      drag: 0,
    })
  }
}

/**
 * A gem breathing light, in three layers.
 *
 * The obvious version — spray faint additive discs and let their random
 * overlap do the pulsing — is what this replaces, and it failed on both
 * counts the effect is judged by. Overlap makes the beat as fast as the
 * spawn rate, so a "magic stone" flickers like a loose contact; and a dozen
 * additive discs alive at once sum past white, which erases the painted
 * facets underneath. A gem you cannot see through its own glow has stopped
 * being a gem.
 *
 * So the light is authored, not accumulated:
 *
 * 1. CORE — one sprite per breath, sized to the stone itself and stretched
 *    to its aspect, so a tall rhombus does not get a round halo bleeding
 *    onto the gold around it. `fog` fade makes each sprite a full sine
 *    breath on its own; the period, not the spawn rate, sets the tempo.
 * 2. BLOOM — the same beat, much wider, an order fainter. This is the part
 *    that touches the surrounding art, and being faint is the whole job:
 *    bloom is what the eye reads as brightness without any pixel of the
 *    subject being overwritten.
 * 3. MOTES + FLASH — a couple of slow rising specks, and a rare short
 *    sparkle on one facet. Nothing leaving a light source makes it read as
 *    a painted highlight; the flash is what sells "precious" rather than
 *    "lit".
 *
 * Peak alpha is deliberately budgeted: core and bloom overlap for at most
 * a third of a breath, so the additive sum stays under ~0.4 even at the
 * top of the pulse.
 */
function spawnGlow(
  state: EmitterState,
  x: number,
  y: number,
  width: number,
  height: number,
  s: number,
  dt: number,
  field: ParticleField,
  flare: ParticleField,
): void {
  const e = state.emitter
  // Portrait stones (a cut rhombus) versus wide clusters — the aura has to
  // follow, or it lights the frame instead of the jewel.
  const aspect = width > 0 ? height / width : 1

  if (state.phase >= state.nextPulse) {
    state.nextPulse = state.phase + e.period
    // Life runs past the period on purpose: consecutive breaths overlap by
    // about a third, so the trough is a dip rather than a blackout. A gem
    // that goes fully dark between beats reads as blinking.
    const life = e.period * 1.35
    field.emit(x, y, {
      vx: 0,
      vy: 0,
      life,
      // Half again as wide as the stone: the shared texture has a hot white
      // centre, and sizing the sprite to the stone puts that centre on the
      // painted facets. Spreading it wider is also how the aura is made to
      // read stronger without being made hotter — the extra opacity lands
      // on the mounting around the gem, not on the gem's own detail.
      size: width * 1.55,
      gravity: 0,
      alpha0: 0.36 * e.alpha,
      color: e.color,
      spin: 0,
      stretch: aspect * 0.95,
      grow: 0.1,
      fade: 'fog',
      drag: 0,
    })
    field.emit(x, y, {
      vx: 0,
      vy: 0,
      life: life * 1.1,
      size: width * 3.6,
      gravity: 0,
      // About a third of the core: any higher and the bloom is a visible
      // disc sitting on the picture instead of light coming off the stone.
      alpha0: 0.14 * e.alpha,
      color: e.color,
      spin: 0,
      // Rounder than the stone — light spreads, edges do not survive it.
      stretch: 1 + (aspect - 1) * 0.45,
      grow: 0.5,
      fade: 'fog',
      drag: 0,
    })
  }

  // The facet flare: a four-point star opening and closing on one facet.
  //
  // This is what carries the effect. Aura and bloom raise the brightness of
  // an area that is already bright paint, so past a certain point they stop
  // adding legibility and start eating the facets; the star adds a SHAPE
  // instead, and the backdrop has none to hide it in. Twice as often as the
  // breath, so there is usually one within a glance of arriving.
  if (state.phase >= state.nextFlash) {
    state.nextFlash = state.phase + e.period * 0.45 + Math.random() * e.period * 0.5
    // On the stone, not centred on it: a flare pinned to the middle every
    // time reads as a UI badge. Off-centre it reads as a facet catching
    // the light, which is the thing being imitated.
    flare.emit(x + (Math.random() - 0.5) * width * 0.3, y - height * (Math.random() * 0.22), {
      vx: 0,
      vy: 0,
      life: 0.85,
      // Wider than the stone — the spikes have to leave it to be spikes.
      size: width * 3.2,
      gravity: 0,
      alpha0: 0.62 * e.alpha,
      color: e.color,
      spin: 0,
      // Blooming outward as it fades: a star that only fades looks
      // switched off, one that grows looks like light arriving.
      grow: 0.45,
      fade: 'fog',
      drag: 0,
    })
    // A hot white pip under the star's centre. The tint alone leaves the
    // flare the same hue as the gem, and a highlight that is not whiter
    // than what it sits on does not read as a highlight.
    field.emit(x, y - height * 0.05, {
      vx: 0,
      vy: 0,
      life: 0.6,
      size: width * 0.55,
      gravity: 0,
      alpha0: 0.55 * e.alpha,
      color: 0xffffff,
      spin: 0,
      grow: -0.6,
      fade: 'fog',
      drag: 0,
    })
  }

  // Motes drift off the stone's own edge, not its centre: a speck born in
  // the middle has to cross the gem to escape, and on the way it reads as
  // a bug crawling over the art.
  state.pending += e.rate * dt
  const count = Math.floor(state.pending)
  state.pending -= count
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2
    field.emit(x + Math.cos(angle) * width * 0.4, y + Math.sin(angle) * height * 0.35, {
      // Leaning away from the stone rather than straight up: motes that all
      // rise vertically read as a column of smoke over the gem.
      vx: Math.cos(angle) * 10 * s + (Math.random() - 0.5) * 8 * s,
      vy: -(8 + Math.random() * 16) * s,
      life: 2.2 + Math.random() * 1.4,
      size: Math.max(2, width * 0.13),
      gravity: 0,
      // Brighter than the aura they leave: a mote is a point, and a point
      // at the aura's own opacity disappears against painted highlights.
      alpha0: (0.5 + Math.random() * 0.35) * e.alpha,
      color: e.color,
      spin: 0,
      // Barely shrinking: a mote that shrinks fast reads as falling away
      // from the camera, which fights the slow rise.
      grow: -0.25,
      fade: 'fog',
      drag: 0.15,
    })
  }
}

/**
 * Clouds crossing the whole scene.
 *
 * The one emitter that is NOT pinned to a point in the art: a cloud is not
 * a feature of the painting, it passes in front of it. So only the band's
 * height comes through the cover transform — horizontally a cloud enters
 * from the viewport edge and leaves by the other one, whatever the crop.
 */
function spawnClouds(
  state: EmitterState,
  fit: Cover,
  screen: Screen,
  dt: number,
  field: ParticleField,
): void {
  const e = state.emitter
  const s = fit.scale
  const bandY = fit.offsetY + e.y * fit.imageHeight * s
  const spread = e.height * fit.imageHeight * s
  const size = e.width * fit.imageWidth * s
  // Sign picks the edge they enter from; magnitude is image widths/second.
  const speed = e.curve * fit.imageWidth * s
  if (speed === 0) return
  // Full-span clouds cross the viewport edge to edge. A corridor is pinned
  // to the IMAGE instead — it exists to keep the clouds off the painted
  // foreground, and that foreground moves with the cover crop.
  const centerX = fit.offsetX + e.x * fit.imageWidth * s
  const travel = e.span >= 1 ? screen.w + size * 2 : e.span * fit.imageWidth * s
  const life = travel / Math.abs(speed)

  const release = (progress: number): void => {
    const fromLeft = speed > 0
    const startX =
      e.span >= 1 ? (fromLeft ? -size : screen.w + size) : centerX + (travel / 2) * (fromLeft ? -1 : 1)
    field.emit(startX + speed * life * progress, bandY + (Math.random() - 0.5) * spread, {
      vx: speed,
      vy: (Math.random() - 0.5) * 3 * s,
      life: life * (1 - progress),
      // Wide size range: with photo sprites the SAME file at half scale
      // reads as a different, more distant cloud — scale is most of the
      // variety a small fleet can offer.
      size: size * (0.55 + Math.random() * 0.9),
      gravity: 0,
      alpha0: (0.7 + Math.random() * 0.3) * e.alpha,
      color: e.color,
      spin: 0,
      // Which cumulus from the fleet — full heaps and flatter banks mix in
      // one band, and a random mirror doubles the apparent variety. Height
      // follows each texture's own aspect.
      tex: Math.floor(Math.random() * field.textureCount),
      flip: Math.random() < 0.5,
      grow: 0.08,
      fade: 'fog',
      drag: 0,
    })
  }

  if (!state.seeded) {
    state.seeded = true
    // Back-fill the sky as if the clouds had been crossing all along.
    const inFlight = Math.max(1, Math.round(e.rate * life))
    for (let i = 0; i < inFlight; i++) release(Math.random())
  }

  state.pending += e.rate * dt
  const count = Math.floor(state.pending)
  state.pending -= count
  for (let i = 0; i < count; i++) release(0)
}
